/**
 * FridgeIQ — Inventory Management
 * Stock levels per SKU, restock by quantity added, restock history.
 * Auto-decrements on checkout via storage.py.
 */

import { useState, useEffect, useCallback } from "react";
import { getInventory, restockItem, getRestockLog } from "../services/dynamodb";

// ── Shared category list — must match ProductLibrary ──────────────────────────
export const BASE_CATEGORIES = [
  "Energy Drink",
  "Electrolyte",
  "Juice",
  "Protein Bar",
  "Protein Shake",
  "Snack",
  "Sports Drink",
  "Water",
];

const STOCK_COLORS = {
  good: {
    bg: "rgba(16,185,129,0.1)",
    border: "rgba(16,185,129,0.25)",
    color: "#10b981",
    label: "Good",
  },
  low: {
    bg: "rgba(245,158,11,0.1)",
    border: "rgba(245,158,11,0.25)",
    color: "#fbbf24",
    label: "Low",
  },
  empty: {
    bg: "rgba(239,68,68,0.1)",
    border: "rgba(239,68,68,0.25)",
    color: "#f87171",
    label: "Empty",
  },
};

function getStockStatus(quantity, threshold) {
  if (quantity <= 0) return "empty";
  if (quantity <= threshold) return "low";
  return "good";
}

function StockBadge({ quantity, threshold }) {
  const status = getStockStatus(quantity, threshold);
  const s = STOCK_COLORS[status];
  return (
    <span
      style={{
        background: s.bg,
        border: `1px solid ${s.border}`,
        color: s.color,
        borderRadius: 6,
        padding: "2px 10px",
        fontSize: 11,
        fontWeight: 600,
      }}
    >
      {status === "empty" ? "🔴" : status === "low" ? "🟡" : "🟢"} {s.label}
    </span>
  );
}

function StockBar({ quantity, threshold, max = 24 }) {
  const pct = Math.min(100, (quantity / max) * 100);
  const status = getStockStatus(quantity, threshold);
  const colors = { good: "#10b981", low: "#f59e0b", empty: "#ef4444" };
  return (
    <div
      style={{
        height: 5,
        background: "rgba(255,255,255,0.06)",
        borderRadius: 3,
        overflow: "hidden",
        width: "100%",
      }}
    >
      <div
        style={{
          height: "100%",
          width: `${pct}%`,
          background: colors[status],
          borderRadius: 3,
          transition: "width 0.4s",
        }}
      />
    </div>
  );
}

function RestockModal({ item, machineId, onClose, onSuccess }) {
  const [qty, setQty] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    const amount = parseInt(qty);
    if (!amount || amount <= 0) {
      setError("Enter a valid quantity");
      return;
    }
    setSaving(true);
    const ok = await restockItem(machineId, item.sku, amount, "operator");
    setSaving(false);
    if (ok) onSuccess();
    else setError("Failed to save — check console");
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.7)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#0f1612",
          border: "1px solid rgba(255,255,255,0.12)",
          borderRadius: 16,
          padding: 28,
          width: 360,
        }}
      >
        <div
          style={{
            fontSize: 16,
            fontWeight: 700,
            color: "#f9fafb",
            marginBottom: 4,
          }}
        >
          Restock Item
        </div>
        <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 20 }}>
          {item.name}
        </div>

        <div style={{ marginBottom: 16 }}>
          <div
            style={{
              fontSize: 11,
              color: "#6b7280",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              marginBottom: 6,
            }}
          >
            Current Stock
          </div>
          <div style={{ fontSize: 28, fontWeight: 700, color: "#f9fafb" }}>
            {item.quantity}
            <span
              style={{
                fontSize: 13,
                color: "#4b5563",
                fontWeight: 400,
                marginLeft: 6,
              }}
            >
              units
            </span>
          </div>
        </div>

        <div style={{ marginBottom: 20 }}>
          <label
            style={{
              fontSize: 12,
              color: "#9ca3af",
              display: "block",
              marginBottom: 6,
            }}
          >
            Units Added
          </label>
          <input
            type="number"
            min="1"
            value={qty}
            onChange={(e) => setQty(e.target.value)}
            placeholder="e.g. 12"
            autoFocus
            style={{
              width: "100%",
              background: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: 8,
              padding: "12px 14px",
              color: "#f9fafb",
              fontSize: 18,
              fontWeight: 700,
              outline: "none",
              textAlign: "center",
            }}
          />
          {qty && parseInt(qty) > 0 && (
            <div
              style={{
                fontSize: 12,
                color: "#10b981",
                marginTop: 6,
                textAlign: "center",
              }}
            >
              New total: {item.quantity + parseInt(qty)} units
            </div>
          )}
        </div>

        {error && (
          <div style={{ color: "#f87171", fontSize: 12, marginBottom: 12 }}>
            ⚠️ {error}
          </div>
        )}

        <div style={{ display: "flex", gap: 10 }}>
          <button
            onClick={onClose}
            style={{
              flex: 1,
              background: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(255,255,255,0.1)",
              color: "#9ca3af",
              borderRadius: 8,
              padding: "10px",
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            style={{
              flex: 1,
              background: "rgba(16,185,129,0.2)",
              border: "1px solid rgba(16,185,129,0.4)",
              color: "#10b981",
              borderRadius: 8,
              padding: "10px",
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            {saving ? "Saving..." : `+ ${qty || "?"} Units`}
          </button>
        </div>
      </div>
    </div>
  );
}

function RestockLog({ machineId }) {
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getRestockLog(machineId).then((items) => {
      setLog(items);
      setLoading(false);
    });
  }, [machineId]);

  if (loading)
    return (
      <div style={{ color: "#4b5563", fontSize: 13, padding: "16px 0" }}>
        Loading history...
      </div>
    );
  if (log.length === 0)
    return (
      <div style={{ color: "#4b5563", fontSize: 13, padding: "16px 0" }}>
        No restock history yet.
      </div>
    );

  return (
    <div>
      {log.map((entry, i) => (
        <div
          key={i}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 16,
            padding: "10px 0",
            borderBottom: "1px solid rgba(255,255,255,0.04)",
            fontSize: 13,
          }}
        >
          <div style={{ color: "#4b5563", minWidth: 140, fontSize: 11 }}>
            {new Date(entry.timestamp).toLocaleString("en-US", {
              month: "short",
              day: "numeric",
              hour: "numeric",
              minute: "2-digit",
              hour12: true,
            })}
          </div>
          <div style={{ flex: 1, color: "#d1d5db" }}>{entry.name}</div>
          <div style={{ color: "#10b981", fontWeight: 600 }}>
            +{entry.qty_added} units
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Inventory({ machineId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restocking, setRestocking] = useState(null);
  const [filter, setFilter] = useState("all");
  const [catFilter, setCatFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [showLog, setShowLog] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const data = await getInventory(machineId);
    data.sort((a, b) => {
      const order = { empty: 0, low: 1, good: 2 };
      const sa = getStockStatus(a.quantity, a.low_stock_threshold || 6);
      const sb = getStockStatus(b.quantity, b.low_stock_threshold || 6);
      if (order[sa] !== order[sb]) return order[sa] - order[sb];
      return a.name?.localeCompare(b.name);
    });
    setItems(data);
    setLoading(false);
  }, [machineId]);

  useEffect(() => {
    load();
  }, [load]);

  // Derive the full category list: BASE_CATEGORIES + any custom ones on actual items
  const itemCategories = [
    ...new Set(items.map((i) => i.category).filter(Boolean)),
  ];
  const allCategories = [
    ...new Set([...BASE_CATEGORIES, ...itemCategories]),
  ].sort();

  const filtered = items.filter((item) => {
    const status = getStockStatus(item.quantity, item.low_stock_threshold || 6);
    if (filter !== "all" && status !== filter) return false;
    if (catFilter !== "all" && item.category !== catFilter) return false;
    if (search && !item.name?.toLowerCase().includes(search.toLowerCase()))
      return false;
    return true;
  });

  const counts = {
    all: items.length,
    good: items.filter(
      (i) => getStockStatus(i.quantity, i.low_stock_threshold || 6) === "good",
    ).length,
    low: items.filter(
      (i) => getStockStatus(i.quantity, i.low_stock_threshold || 6) === "low",
    ).length,
    empty: items.filter(
      (i) => getStockStatus(i.quantity, i.low_stock_threshold || 6) === "empty",
    ).length,
  };

  const totalUnits = items.reduce((s, i) => s + (i.quantity || 0), 0);

  if (loading)
    return (
      <div style={{ padding: 40, textAlign: "center", color: "#4b5563" }}>
        Loading inventory...
      </div>
    );

  return (
    <div>
      {/* Summary */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 10,
          marginBottom: 20,
        }}
      >
        {[
          { label: "Total SKUs", value: items.length, accent: "#f9fafb" },
          { label: "Total Units", value: totalUnits, accent: "#60a5fa" },
          { label: "Low Stock", value: counts.low, accent: "#fbbf24" },
          {
            label: "Out of Stock",
            value: counts.empty,
            accent: counts.empty > 0 ? "#f87171" : "#10b981",
          },
        ].map((s, i) => (
          <div
            key={i}
            style={{
              background: "rgba(255,255,255,0.03)",
              border: "1px solid rgba(255,255,255,0.07)",
              borderRadius: 10,
              padding: "12px 16px",
            }}
          >
            <div
              style={{
                fontSize: 10,
                color: "#6b7280",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                marginBottom: 4,
              }}
            >
              {s.label}
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: s.accent }}>
              {s.value}
            </div>
          </div>
        ))}
      </div>

      {/* Filter bar */}
      <div
        style={{
          display: "flex",
          gap: 6,
          marginBottom: 12,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        {/* Stock status filters */}
        {["all", "good", "low", "empty"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            style={{
              background:
                filter === f
                  ? "rgba(16,185,129,0.15)"
                  : "rgba(255,255,255,0.04)",
              border: `1px solid ${filter === f ? "rgba(16,185,129,0.35)" : "rgba(255,255,255,0.08)"}`,
              color: filter === f ? "#10b981" : "#6b7280",
              borderRadius: 8,
              padding: "5px 12px",
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
            <span style={{ marginLeft: 5, opacity: 0.7 }}>
              ({counts[f] ?? items.length})
            </span>
          </button>
        ))}

        <div
          style={{
            width: 1,
            height: 20,
            background: "rgba(255,255,255,0.1)",
            margin: "0 4px",
          }}
        />

        {/* Category filters — same list as ProductLibrary */}
        <button
          onClick={() => setCatFilter("all")}
          style={{
            background:
              catFilter === "all"
                ? "rgba(96,165,250,0.15)"
                : "rgba(255,255,255,0.03)",
            border: `1px solid ${catFilter === "all" ? "rgba(96,165,250,0.35)" : "rgba(255,255,255,0.06)"}`,
            color: catFilter === "all" ? "#60a5fa" : "#4b5563",
            borderRadius: 8,
            padding: "5px 12px",
            fontSize: 11,
            fontWeight: 600,
          }}
        >
          🏪 All
        </button>
        {allCategories.map((cat) => (
          <button
            key={cat}
            onClick={() => setCatFilter(cat)}
            style={{
              background:
                catFilter === cat
                  ? "rgba(96,165,250,0.15)"
                  : "rgba(255,255,255,0.03)",
              border: `1px solid ${catFilter === cat ? "rgba(96,165,250,0.35)" : "rgba(255,255,255,0.06)"}`,
              color: catFilter === cat ? "#60a5fa" : "#4b5563",
              borderRadius: 8,
              padding: "5px 12px",
              fontSize: 11,
              fontWeight: 600,
            }}
          >
            {cat}
            {items.filter((i) => i.category === cat).length > 0 && (
              <span style={{ marginLeft: 5, opacity: 0.5 }}>
                ({items.filter((i) => i.category === cat).length})
              </span>
            )}
          </button>
        ))}

        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search..."
          style={{
            marginLeft: "auto",
            background: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: 8,
            padding: "5px 12px",
            color: "#f9fafb",
            fontSize: 12,
            outline: "none",
            width: 160,
          }}
        />
        <button
          onClick={() => setShowLog(!showLog)}
          style={{
            background: showLog
              ? "rgba(96,165,250,0.15)"
              : "rgba(255,255,255,0.04)",
            border: `1px solid ${showLog ? "rgba(96,165,250,0.3)" : "rgba(255,255,255,0.08)"}`,
            color: showLog ? "#60a5fa" : "#6b7280",
            borderRadius: 8,
            padding: "5px 12px",
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          📋 Log
        </button>
        <button
          onClick={load}
          style={{
            background: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.08)",
            color: "#6b7280",
            borderRadius: 8,
            padding: "5px 10px",
            fontSize: 13,
          }}
        >
          ↻
        </button>
      </div>

      {/* Restock log */}
      {showLog && (
        <div
          style={{
            background: "rgba(255,255,255,0.02)",
            border: "1px solid rgba(255,255,255,0.07)",
            borderRadius: 12,
            padding: "16px 20px",
            marginBottom: 16,
          }}
        >
          <div
            style={{
              fontSize: 12,
              color: "#6b7280",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              marginBottom: 10,
            }}
          >
            Restock History
          </div>
          <RestockLog machineId={machineId} />
        </div>
      )}

      {/* Inventory table */}
      <div
        style={{
          background: "rgba(255,255,255,0.02)",
          border: "1px solid rgba(255,255,255,0.07)",
          borderRadius: 12,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "2fr 1fr 1fr 100px 80px",
            gap: 12,
            padding: "10px 18px",
            borderBottom: "1px solid rgba(255,255,255,0.06)",
            fontSize: 11,
            color: "#4b5563",
            textTransform: "uppercase",
            letterSpacing: "0.06em",
          }}
        >
          <div>Product</div>
          <div>Stock</div>
          <div>Level</div>
          <div>Status</div>
          <div></div>
        </div>

        {filtered.length === 0 ? (
          <div
            style={{
              padding: 32,
              textAlign: "center",
              color: "#4b5563",
              fontSize: 13,
            }}
          >
            No items found
          </div>
        ) : (
          filtered.map((item, i) => {
            const status = getStockStatus(
              item.quantity,
              item.low_stock_threshold || 6,
            );
            return (
              <div
                key={item.sku}
                style={{
                  display: "grid",
                  gridTemplateColumns: "2fr 1fr 1fr 100px 80px",
                  gap: 12,
                  padding: "14px 18px",
                  alignItems: "center",
                  borderBottom:
                    i < filtered.length - 1
                      ? "1px solid rgba(255,255,255,0.04)"
                      : "none",
                  background:
                    status === "empty" ? "rgba(239,68,68,0.03)" : "transparent",
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: 13,
                      color: "#f9fafb",
                      fontWeight: 500,
                      marginBottom: 2,
                    }}
                  >
                    {item.name}
                  </div>
                  <div style={{ fontSize: 11, color: "#4b5563" }}>
                    {item.category} · SKU {item.sku}
                  </div>
                </div>
                <div
                  style={{
                    fontSize: 18,
                    fontWeight: 700,
                    color:
                      status === "empty"
                        ? "#f87171"
                        : status === "low"
                          ? "#fbbf24"
                          : "#f9fafb",
                  }}
                >
                  {item.quantity}
                  <span
                    style={{
                      fontSize: 11,
                      color: "#4b5563",
                      fontWeight: 400,
                      marginLeft: 4,
                    }}
                  >
                    units
                  </span>
                </div>
                <div style={{ paddingRight: 8 }}>
                  <StockBar
                    quantity={item.quantity}
                    threshold={item.low_stock_threshold || 6}
                  />
                  <div style={{ fontSize: 10, color: "#4b5563", marginTop: 3 }}>
                    threshold: {item.low_stock_threshold || 6}
                  </div>
                </div>
                <StockBadge
                  quantity={item.quantity}
                  threshold={item.low_stock_threshold || 6}
                />
                <button
                  onClick={() => setRestocking(item)}
                  style={{
                    background: "rgba(16,185,129,0.1)",
                    border: "1px solid rgba(16,185,129,0.25)",
                    color: "#10b981",
                    borderRadius: 7,
                    padding: "5px 12px",
                    fontSize: 12,
                    fontWeight: 600,
                    width: "100%",
                  }}
                >
                  + Add
                </button>
              </div>
            );
          })
        )}
      </div>

      {restocking && (
        <RestockModal
          item={restocking}
          machineId={machineId}
          onClose={() => setRestocking(null)}
          onSuccess={() => {
            setRestocking(null);
            load();
          }}
        />
      )}
    </div>
  );
}
